import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Gavel } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { pctVsListBand, priceBand, track } from '@siliconx/shared/analytics';
import { api, throwIfNotOk } from '../lib/api';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import { ErrorState, LoadingShell } from '@siliconx/shared/ui/state';
import { ListingStatusBadge } from '@siliconx/shared/ui/ListingStatusBadge';
import { OfferStatusBadge } from '@siliconx/shared/ui/OfferStatusBadge';
import { AuctionCountdown } from '@siliconx/shared/ui/AuctionCountdown';
import type {
  ListingDetail,
  ListingDetailOffer,
  ListingLotEnvironmental,
} from '@siliconx/shared/dto/listing';
import type { VerifiedPricing, UnverifiedPricing, AskPricing } from '@siliconx/shared/dto/marketplace';
import { priceText } from '@siliconx/shared/ui/buyer-listing/formatPriceRange';
import { formatDate } from '../lib/format';
import { isFutureDate, formatMonthYear, formatDayMonthYear } from '../lib/dates';
import { useAuth } from '../lib/authContext';
import { ProfileLockedCard } from '../components/ProfileLockedCard';
import { ManufacturerLogo } from '../components/shared/ManufacturerLogo';
import { EvidencePanel } from '@siliconx/shared/ui/buyer-listing/EvidencePanel';
import { PassportSummaryStrip } from '../components/passport/PassportSurface';
import { CarbonClaim } from '../components/shared/CarbonClaim';
import { CARBON_EMBODIED_EXPLAINER, hasOrgFeature } from '@siliconx/shared';
import { qk } from '../lib/queryKeys';
import { Card } from '@siliconx/shared/ui/Card';
import { TextInput } from '@siliconx/shared/ui/Field';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { BackLink } from '@siliconx/shared/ui/BackLink';
import { EscrowAgentDisclosure } from '../components/exchange/EscrowAgentDisclosure';
import { ExpressInterestModal } from '../components/exchange/ExpressInterestModal';
import { ExpressedInterestsPanel } from '../components/exchange/ExpressedInterestsPanel';
import { ListingNotesPanel } from '../components/exchange/ListingNotesPanel';
import { nodeCountOf } from '@siliconx/shared/ui/buyer-listing/listingShape';
import { BuyerListingView } from '@siliconx/shared/ui/buyer-listing/BuyerListingView';
import { VerdictBadge } from '@siliconx/shared/ui/buyer-listing/LotContentsTable';
import { NodePassportDrawer } from '../components/exchange/NodePassportDrawer';
import { useVerificationGate } from '../components/exchange/VerificationGateProvider';
import { isBuyerVerified, useBuyerStatus } from '../hooks/useBuyerStatus';
import {
  LISTING_BACK_LABEL,
  LOT_TABLE_PASSPORT_ACTION,
} from '@siliconx/shared/ui/buyer-listing/copy/listingDetail';
import {
  createPurchaseTermsConsent,
  isPurchaseTermsConsentRequiredError,
} from '../lib/purchaseTerms';
import {
  PURCHASE_TERMS_BODY,
  PURCHASE_TERMS_CHECKBOX_LABEL,
  PURCHASE_TERMS_CONSENT_ERROR,
  PURCHASE_TERMS_HEADING,
} from '../components/exchange/copy/purchaseTerms';
import {
  ACTION_CONFIRM_AVAILABILITY,
  ACTION_DECLINE,
  BUY_NOW_CONFIRM_LABEL,
  BUY_NOW_CONFIRM_PENDING_LABEL,
  BUY_NOW_MODAL_TITLE,
  BUY_NOW_REQUEST_LABEL,
  BUY_NOW_ROW_LABELS,
  BUY_NOW_SUCCESS_TOAST,
  BUY_NOW_TERMS_NOTE,
  buyNowTimeLeftLabel,
  EXPRESS_INTEREST_BUTTON,
  MAKE_OFFER_AMOUNT_LABEL,
  MAKE_OFFER_BINDING_NOTE,
  MAKE_OFFER_MODAL_TITLE,
  MAKE_OFFER_SUBMIT_LABEL,
  MAKE_OFFER_SUBMIT_PENDING_LABEL,
  MAKE_OFFER_TILE_LABELS,
  offerPrefillUsd,
  bandMidpointOfferPrefillUsd,
  pctVsLabel,
} from '@siliconx/shared/ui/buyer-listing/copy/listingDetail';
import { apiErrorCode, exchangeErrorMessage } from '../components/exchange/errors';
import { formatUsd } from '../lib/format';
import { listingPriceView } from '@siliconx/shared/ui/buyer-listing/listingPriceView';

export const Route = createFileRoute('/listings_/$listingId')({
  component: ListingDetailPage,
});

// ---------------------------------------------------------------------------
// Accept confirmation dialog — owner-only, unchanged (SIL-1348 leaves the
// owner's accept/reject flow exactly as it was).
// ---------------------------------------------------------------------------

/**
 * SIL-2276: seller-visible per-offer label, derived from the offer's own id.
 * Never the buyer org id (stable across deals; lets a seller recognize a
 * repeat buyer).
 */
function offerLabel(offerId: string): string {
  return `Offer #${offerId.slice(0, 8)}`;
}

function AcceptDialog({
  offer,
  availableOn,
  onConfirm,
  onCancel,
  isPending,
}: {
  offer: ListingDetailOffer;
  /** 669: the listing's availableOn — a future date makes accept a BINDING forward commitment. */
  availableOn?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const forwardDated = isFutureDate(availableOn);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="max-w-sm shadow-lg w-full">
        <h2 className="text-base font-semibold text-foreground">
          {forwardDated ? 'Accept this forward-sale offer?' : 'Accept this offer?'}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Accept offer for{' '}
          <span className="font-medium">
            ${offer.amountUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>{' '}
          from{' '}
          <span className="font-mono text-xs">{offerLabel(offer.id)}</span>?
          This will move the listing to{' '}
          <span className="font-medium">Offer pending</span>.
        </p>
        {forwardDated && (
          <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
            <p>
              <span className="font-semibold">
                Settlement date: {formatDayMonthYear(availableOn!)}.
              </span>{' '}
              Once the buyer pays the deposit, this deal is binding — the deposit is held by
              Escrow.com until settlement and there is no self-serve cancellation. If you cannot
              deliver, Escrow.com returns the buyer&rsquo;s deposit under the escrow instructions.
            </p>
            <EscrowAgentDisclosure className="mt-2 text-xs text-indigo-800" />
          </div>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {isPending
              ? 'Accepting…'
              : forwardDated
                ? `Accept — settles ${formatMonthYear(availableOn!)}`
                : 'Accept'}
          </button>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-offer action buttons (pending offers only) — owner-only. SIL-1427: a
// kind='buy_now' row relabels Accept/Reject to Confirm availability/Decline
// (spec section 5) — never Counter, which this component has never offered
// for ANY offer, buy_now included.
// ---------------------------------------------------------------------------

function OfferActions({
  offer,
  listingId,
  availableOn,
}: {
  offer: ListingDetailOffer;
  listingId: string;
  availableOn?: string | null;
}) {
  const queryClient = useQueryClient();
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.offers[':id'].$patch({
        param: { id: offer.id },
        json: { status: 'accepted' },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Offer accepted.');
      setShowAcceptDialog(false);
      void queryClient.invalidateQueries({ queryKey: qk.listings.detail(listingId) });
      void queryClient.invalidateQueries({ queryKey: qk.listings.all });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Accept failed.'),
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.offers[':id'].$patch({
        param: { id: offer.id },
        json: { status: 'rejected' },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Offer rejected.');
      void queryClient.invalidateQueries({ queryKey: qk.listings.detail(listingId) });
      void queryClient.invalidateQueries({ queryKey: qk.listings.all });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Reject failed.'),
  });

  if (offer.status !== 'pending') return null;

  const isBusy = acceptMutation.isPending || rejectMutation.isPending;

  return (
    <>
      {showAcceptDialog && (
        <AcceptDialog
          offer={offer}
          availableOn={availableOn}
          onConfirm={() => acceptMutation.mutate()}
          onCancel={() => setShowAcceptDialog(false)}
          isPending={acceptMutation.isPending}
        />
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setShowAcceptDialog(true)}
          disabled={isBusy}
          className="rounded border border-green-300 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
        >
          {offer.kind === 'buy_now' ? ACTION_CONFIRM_AVAILABILITY : 'Accept'}
        </button>
        <button
          type="button"
          onClick={() => rejectMutation.mutate()}
          disabled={isBusy}
          className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
        >
          {rejectMutation.isPending ? '…' : offer.kind === 'buy_now' ? ACTION_DECLINE : 'Reject'}
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Buy now dialog (buyer) — SIL-1427 (spec section 5 / handoff section 6).
// Replaces the SIL-1318 interim rule where the Buy now button opened
// MakeOfferDialog instead. The route accepts NO amount (tg_offer_buy_now_
// insert, migration 972, sets it server-side from the listing's current
// price) — this dialog never sends one.
//
// Error mapping (per the issue):
//   409 PURCHASE_PENDING — refetch the listing and close; the ActionCard's
//     own "Purchase pending" caption takes over, not an error toast.
//   409 LISTING_NOT_AVAILABLE — a plain toast; the listing went inactive or
//     is no longer buy_now underneath the buyer.
//   403 BUYER_VERIFICATION_REQUIRED — routed through useVerificationGate's
//     `guard`, same as every other gated buyer action.
// ---------------------------------------------------------------------------

function BuyNowDialog({
  listing,
  onClose,
}: {
  listing: ListingDetail;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { guard } = useVerificationGate();
  const pricing = listing.pricing as VerifiedPricing;
  const nodeCount = nodeCountOf(listing);

  const buyNowMutation = useMutation({
    mutationFn: async () => {
      return guard(async () => {
        const res = await api.api.client.listings[':id']['buy-now'].$post({
          param: { id: listing.id },
        });
        await throwIfNotOk(res);
        return true as const;
      });
    },
    onSuccess: (submitted) => {
      // `undefined` means the gate handled a 403 — nothing was submitted.
      if (submitted === undefined) {
        onClose();
        return;
      }
      track('buy_now_submit', { item_id: listing.id, result: 'success' });
      toast.success(BUY_NOW_SUCCESS_TOAST);
      void queryClient.invalidateQueries({ queryKey: qk.listings.detail(listing.id) });
      void queryClient.invalidateQueries({ queryKey: qk.listings.all });
      onClose();
    },
    onError: (err: Error) => {
      const errorCode = apiErrorCode(err);
      if (errorCode === 'PURCHASE_PENDING') {
        track('buy_now_submit', { item_id: listing.id, result: 'purchase_pending' });
        // Not an error toast (per the issue) — refetch so the ActionCard's
        // own "Purchase pending" caption takes over the explanation.
        void queryClient.invalidateQueries({ queryKey: qk.listings.detail(listing.id) });
        onClose();
        return;
      }
      track('form_error', { form: 'buy_now', reason: 'api', error_code: errorCode ?? 'unknown' });
      toast.error(exchangeErrorMessage(err, 'Failed to send the purchase request.'));
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="max-w-sm shadow-lg w-full">
        <h2 className="text-base font-semibold text-foreground">{BUY_NOW_MODAL_TITLE}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {listing.displayTitle ?? listing.skuModelName ?? listing.id.slice(0, 8)}
        </p>
        <div className="mt-4 divide-y divide-border border-y border-border">
          <div className="flex items-baseline justify-between gap-4 py-1.5">
            <span className="text-xs text-muted-foreground">{BUY_NOW_ROW_LABELS.quantity}</span>
            <span className="font-num text-sm text-foreground">{nodeCount}</span>
          </div>
          <div className="flex items-baseline justify-between gap-4 py-1.5">
            <span className="text-xs text-muted-foreground">{BUY_NOW_ROW_LABELS.pricePerGpu}</span>
            <span className="font-num text-sm text-foreground">
              {pricing.pricePerGpuUsd === null ? 'n/a' : formatUsd(pricing.pricePerGpuUsd)}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-4 py-1.5">
            <span className="text-xs text-muted-foreground">{BUY_NOW_ROW_LABELS.pricePerNode}</span>
            <span className="font-num text-sm text-foreground">
              {formatUsd(pricing.pricePerNodeUsd)}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-4 py-1.5">
            <span className="text-xs font-medium text-foreground">{BUY_NOW_ROW_LABELS.total}</span>
            <span className="font-num text-sm font-semibold text-foreground">
              {formatUsd(pricing.totalUsd)}
            </span>
          </div>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">{BUY_NOW_TERMS_NOTE}</p>
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={buyNowMutation.isPending}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => buyNowMutation.mutate()}
            disabled={buyNowMutation.isPending}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {buyNowMutation.isPending ? BUY_NOW_CONFIRM_PENDING_LABEL : BUY_NOW_CONFIRM_LABEL}
          </button>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Make Offer dialog (buyer) — relaid out per SIL-1427 (spec section 5 /
// handoff section 6): prefilled at 97% of the list price rounded to $100,
// live tiles for the lot total, vs. list price and vs. FMV median, and the
// 72h-binding copy.
//
// The spec also lists an optional note. Deliberately NOT built here: `offer`
// has no note/message column (confirmed against db.d.ts) and this child
// ships no migration, so a text field here would collect input, then
// silently discard it on submit — no error, no trace, the seller never sees
// it. That is worse than not offering the field. Tracked as SIL-1457 (the
// offer.note column, the API accepting it, and the seller seeing it) —
// re-add only alongside that, never as a UI-only repeat of this.
//
// The POST runs through `useVerificationGate().guard`, so a 403
// BUYER_VERIFICATION_REQUIRED opens the verification modal instead of
// surfacing as a raw error toast. The server stays the boundary; this only
// reacts to it.
// ---------------------------------------------------------------------------

function MakeOfferDialog({
  listingId,
  pricing,
  onClose,
}: {
  listingId: string;
  pricing: VerifiedPricing | UnverifiedPricing | AskPricing;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { guard } = useVerificationGate();
  // ActionCard opens this dialog from the verified-tier branch (exact list
  // price always available), SIL-1472's offers-only branch of a band listing
  // — there `pricing.tier` is 'unverified' (the exact price is owner-only
  // for a band listing, even for a verified caller), so the prefill comes
  // from the band total's midpoint instead of 97% of a list price that does
  // not exist for this caller — OR SIL-1480's 'ask' mode, where the exact
  // price is always available (to every caller), same as verified.
  const view = listingPriceView(pricing);
  const listPriceUsd = view.listPriceUsd;
  const fmvMedianUsd = view.fmvMedian;
  const bandPrefillUsd =
    view.totalRange !== null
      ? bandMidpointOfferPrefillUsd(view.totalRange.lo, view.totalRange.hi)
      : null;
  const [amountStr, setAmountStr] = useState(
    listPriceUsd != null
      ? String(offerPrefillUsd(listPriceUsd))
      : bandPrefillUsd != null
        ? String(bandPrefillUsd)
        : '',
  );

  const makeMutation = useMutation({
    mutationFn: async () => {
      const amountUsd = parseFloat(amountStr);
      return guard(async () => {
        const res = await api.api.client.offers.$post({ json: { listingId, amountUsd } });
        await throwIfNotOk(res);
        return true as const;
      });
    },
    onSuccess: (submitted) => {
      // `undefined` means the gate handled a 403 and already showed the modal
      // or the toast — nothing was submitted, so do not claim it was.
      if (submitted === undefined) {
        onClose();
        return;
      }
      track('make_offer_submit', { item_id: listingId, pct_vs_list_band: pctVsListBand(parseFloat(amountStr), listPriceUsd) });
      toast.success('Offer submitted.');
      void queryClient.invalidateQueries({ queryKey: qk.listings.detail(listingId) });
      void queryClient.invalidateQueries({ queryKey: qk.listings.all });
      onClose();
    },
    onError: (err: Error) => {
      track('form_error', { form: 'offer', reason: 'api', error_code: apiErrorCode(err) ?? 'unknown' });
      toast.error(err.message ?? 'Failed to submit offer.');
    },
  });

  const amount = parseFloat(amountStr);
  const isValid = !isNaN(amount) && amount > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="max-w-sm shadow-lg w-full">
        <h2 className="text-base font-semibold text-foreground">{MAKE_OFFER_MODAL_TITLE}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Asking price: <span className="font-medium text-foreground">{priceText(pricing)}</span>
        </p>
        <div className="mt-4">
          <label
            htmlFor="offer-amount"
            className="block text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            {MAKE_OFFER_AMOUNT_LABEL}
          </label>
          <TextInput className="focus:border-violet-500 focus:ring-violet-500 mt-1 placeholder-muted-foreground text-foreground"
            id="offer-amount"
            type="number"
            min="0.01"
            step="0.01"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="0.00"
          />
        </div>
        {/* Live tiles — recomputed on every keystroke from the CURRENT
            amountStr, never from the prefill. */}
        <div className="mt-3 grid grid-cols-3 gap-2 rounded-md border border-border bg-muted p-3 text-center">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {MAKE_OFFER_TILE_LABELS.lotTotal}
            </p>
            <p className="font-num text-sm font-medium text-foreground">
              {isValid ? formatUsd(amount) : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {MAKE_OFFER_TILE_LABELS.vsListPrice}
            </p>
            <p className="font-num text-sm font-medium text-foreground">
              {isValid && listPriceUsd != null ? pctVsLabel(amount, listPriceUsd) : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {MAKE_OFFER_TILE_LABELS.vsFmvMedian}
            </p>
            <p className="font-num text-sm font-medium text-foreground">
              {isValid && fmvMedianUsd != null ? pctVsLabel(amount, fmvMedianUsd) : '—'}
            </p>
          </div>
        </div>
        {/*
          SIL-1427: the spec's optional note is deliberately absent here —
          see this component's header comment for why (no `offer.note`
          column to send it to; tracked as SIL-1457).
        */}
        <p className="mt-3 text-xs text-muted-foreground">{MAKE_OFFER_BINDING_NOTE}</p>
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={makeMutation.isPending}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => makeMutation.mutate()}
            disabled={!isValid || makeMutation.isPending}
            className="rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {makeMutation.isPending ? MAKE_OFFER_SUBMIT_PENDING_LABEL : MAKE_OFFER_SUBMIT_LABEL}
          </button>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Your-offer panel (buyer). SIL-1348 moved the "Make Offer" CTA into the
// right-column ActionCard, so this panel is now purely the STATE of the
// caller's own offer: amount, status, submitted date and Withdraw. It renders
// nothing when the caller has no offer — an empty "you have not made an offer"
// card next to an action card that says the same thing is noise.
// ---------------------------------------------------------------------------

function YourOfferPanel({
  listing,
  canBuy,
  buyerMissingFields,
}: {
  listing: ListingDetail;
  canBuy: boolean;
  buyerMissingFields: string[];
}) {
  const queryClient = useQueryClient();
  const myOffer = listing.offers[0] ?? null;

  const withdrawMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.offers[':id'].$patch({
        param: { id: myOffer!.id },
        json: { status: 'withdrawn' },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Offer withdrawn.');
      void queryClient.invalidateQueries({ queryKey: qk.listings.detail(listing.id) });
      void queryClient.invalidateQueries({ queryKey: qk.listings.all });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Withdraw failed.'),
  });

  // 669: a strictly-future availableOn makes any offer a FORWARD-sale
  // commitment — the callout carries the delivery date so nobody bids blind.
  const forwardDated = isFutureDate(listing.availableOn);

  if (myOffer == null) {
    // The profile gate still has to be visible somewhere: without a complete
    // buyer profile the ActionCard's buttons lead to a 4xx.
    if (!canBuy) {
      return (
        <section>
          <PanelTitle title="Your offer" className="mb-3" />
          <ProfileLockedCard side="buyer" missingFields={buyerMissingFields} />
        </section>
      );
    }
    return forwardDated ? (
      <div className="flex items-start gap-2.5 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
        <CalendarClock className="mt-0.5 size-4 shrink-0 text-indigo-600" aria-hidden="true" />
        <p className="text-sm text-indigo-900">
          <span className="font-semibold">
            Not available until {formatDayMonthYear(listing.availableOn!)}
          </span>{' '}
          — offers commit to a forward sale settling then.
        </p>
      </div>
    ) : null;
  }

  return (
    <section>
      <PanelTitle title="Your offer" className="mb-3" />
      {forwardDated && (
        <div className="mb-3 flex items-start gap-2.5 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
          <CalendarClock className="mt-0.5 size-4 shrink-0 text-indigo-600" aria-hidden="true" />
          <p className="text-sm text-indigo-900">
            <span className="font-semibold">
              Not available until {formatDayMonthYear(listing.availableOn!)}
            </span>{' '}
            — offers commit to a forward sale settling then.
          </p>
        </div>
      )}
      <Card>
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Amount:</span>
              <UsdCell value={myOffer.amountUsd} className="text-sm font-medium" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              {/* SIL-322-adjacent: the listing-detail offers[] shape
                  (ListingDetailOffer) does not carry refusalReason — the
                  API's embedded offer summary never selected that column
                  (see @siliconx/shared/dto/listing). */}
              <OfferStatusBadge value={myOffer.status} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Submitted:</span>
              <span className="text-sm text-foreground">{formatDate(myOffer.createdAt)}</span>
            </div>
          </div>
          {myOffer.status === 'pending' && (
            <button
              type="button"
              onClick={() => withdrawMutation.mutate()}
              disabled={withdrawMutation.isPending}
              className="shrink-0 rounded border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
              {withdrawMutation.isPending ? 'Withdrawing…' : 'Withdraw'}
            </button>
          )}
        </div>
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Bid section (buyer) — SIL-647, unchanged by SIL-1348 (spec 4.2: "the auction
// bid dialog stays as it is"). A seller viewing its own auction listing gets
// the read-only variant (seller cannot bid on its own listing — place_bid()
// enforces this server-side too).
//
// Bidders are anonymized to each other: this panel NEVER shows any other
// bidder's identity, only the aggregate current high bid + the caller's own
// position (listing.auctionBidStatus, sourced via client_listing_auction_
// bid_status(), 765's SECURITY DEFINER accessor).
// ---------------------------------------------------------------------------

/**
 * SIL-1424: the purchase-terms consent is taken IMMEDIATELY before the bid —
 * 966 matches `place_bid`'s consent check on (listing_id, bid_amount_usd,
 * customer_org_id) inside a 15-minute window, so a consent taken at dialog
 * open and left sitting could expire before the buyer clicks Place bid. This
 * mutation sends the consent and the bid back to back in one click, never on
 * dialog open, so the two are always seconds apart.
 */
function PlaceBidDialog({
  listingId,
  minNextBidUsd,
  bidVisibility,
  onClose,
}: {
  listingId: string;
  minNextBidUsd: number | null;
  /** SIL-653: sealed auctions have no minimum-increment rule — bidders
   * cannot see the current high bid, so there is nothing to advertise as a
   * floor. `minNextBidUsd` is always null for a sealed listing, but the
   * copy below distinguishes "sealed, no floor by design" from "open, no
   * bids yet". */
  bidVisibility: 'open' | 'sealed';
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [amountStr, setAmountStr] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  const bidMutation = useMutation({
    mutationFn: async () => {
      const amountUsd = parseFloat(amountStr);
      // Consent FIRST, at the moment of submit — not at dialog open — so it
      // is always inside 966's 15-minute match window. The bid call below is
      // unreachable without a `consentId` back from this.
      const consent = await createPurchaseTermsConsent({
        bindingEvent: 'auction_bid',
        listingId,
        bidAmountUsd: amountUsd.toFixed(2),
      });
      if (!consent.consentId) {
        throw new Error('Purchase terms consent was not recorded.');
      }
      const res = await api.api.client.listings[':id'].bids.$post({
        param: { id: listingId },
        json: { amountUsd },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Bid placed.');
      void queryClient.invalidateQueries({ queryKey: qk.listings.detail(listingId) });
      onClose();
    },
    onError: (err: Error) => {
      // 966's PT001 rejection lands here, not in a generic toast — send the
      // buyer back to the terms step to re-accept and retry.
      if (isPurchaseTermsConsentRequiredError(err)) {
        setAgreedToTerms(false);
        setTermsError(PURCHASE_TERMS_CONSENT_ERROR);
        return;
      }
      toast.error(err.message ?? 'Failed to place bid.');
    },
  });

  const amount = parseFloat(amountStr);
  const minOk = minNextBidUsd == null || amount >= minNextBidUsd;
  const isValid = !isNaN(amount) && amount > 0 && minOk;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="max-w-sm shadow-lg w-full">
        <h2 className="text-base font-semibold text-foreground">Place a bid</h2>
        {bidVisibility === 'sealed' ? (
          <p className="mt-1 text-sm text-muted-foreground">
            Sealed auction — bids are private. You won&apos;t see the current high bid, and there is
            no minimum increment to beat; bid what you&apos;re willing to pay.
          </p>
        ) : minNextBidUsd != null ? (
          <p className="mt-1 text-sm text-muted-foreground">
            Minimum next bid:{' '}
            <span className="font-medium text-foreground">
              ${minNextBidUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </p>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">No bids yet — be the first.</p>
        )}
        <div className="mt-4">
          <label
            htmlFor="bid-amount"
            className="block text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            Your bid (USD)
          </label>
          <TextInput
            className="focus:border-violet-500 focus:ring-violet-500 mt-1 placeholder-muted-foreground text-foreground"
            id="bid-amount"
            type="number"
            min={minNextBidUsd ?? 0.01}
            step="0.01"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="0.00"
          />
          {!minOk && amountStr !== '' && (
            <p className="mt-1 text-xs text-red-600">
              Must be at least ${minNextBidUsd!.toLocaleString(undefined, { minimumFractionDigits: 2 })}.
            </p>
          )}
        </div>
        <div className="mt-4 rounded-md border border-border bg-muted p-3 text-sm">
          <p className="font-medium text-foreground">{PURCHASE_TERMS_HEADING}</p>
          <p className="mt-1 text-muted-foreground">{PURCHASE_TERMS_BODY}</p>
          <label className="mt-2 flex items-start gap-2 text-foreground">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => {
                setAgreedToTerms(e.target.checked);
                setTermsError(null);
              }}
              className="mt-0.5"
            />
            <span>{PURCHASE_TERMS_CHECKBOX_LABEL}</span>
          </label>
          {termsError && <p className="mt-1 text-xs text-red-600">{termsError}</p>}
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={bidMutation.isPending}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => bidMutation.mutate()}
            disabled={!isValid || !agreedToTerms || bidMutation.isPending}
            className="rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {bidMutation.isPending ? 'Placing…' : 'Place bid'}
          </button>
        </div>
      </Card>
    </div>
  );
}

function BidSection({
  listing,
  canBuy,
  buyerMissingFields,
  isOwnListing = false,
}: {
  listing: ListingDetail;
  canBuy: boolean;
  buyerMissingFields: string[];
  /** Seller viewing its own auction — read-only, no "Place bid" CTA (a
   * seller cannot bid on its own listing; place_bid() also enforces this
   * server-side). */
  isOwnListing?: boolean;
}) {
  const [showBidDialog, setShowBidDialog] = useState(false);
  const status = listing.auctionBidStatus;
  const closesAt = listing.auction?.closesAt;
  const isOpen =
    !isOwnListing && (listing.status === 'active' || listing.status === 'under_offer') && !!closesAt;
  // SIL-653: a sealed auction reveals nothing but the caller's own bid until
  // it closes (client_listing_auction_bid_status() withholds the aggregate
  // entirely pre-close — server-enforced, this is a rendering choice on top
  // of what the server already returns, not the boundary itself).
  const isSealedPreClose = status?.bidVisibility === 'sealed' && status?.closedAt == null;

  return (
    <div>
      {showBidDialog && (
        <PlaceBidDialog
          listingId={listing.id}
          minNextBidUsd={status?.minNextBidUsd ?? null}
          bidVisibility={status?.bidVisibility ?? 'open'}
          onClose={() => setShowBidDialog(false)}
        />
      )}

      <PanelTitle title="Bidding" className="mb-3" />

      <Card>
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            {isSealedPreClose ? (
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800">
                  Sealed bid
                </span>
                <span className="text-sm text-muted-foreground">
                  Bids are private — visible to {isOwnListing ? 'you' : 'the seller'} only after close.
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {status?.closedAt != null ? 'Final high bid:' : 'Current high bid:'}
                </span>
                {status?.highBidUsd != null ? (
                  <UsdCell value={status.highBidUsd} className="text-sm font-medium" />
                ) : (
                  <span className="text-sm text-foreground">No bids yet</span>
                )}
                {status?.bidCount != null && status.bidCount > 0 && (
                  <span className="text-xs text-muted-foreground">
                    ({status.bidCount} bid{status.bidCount === 1 ? '' : 's'})
                  </span>
                )}
              </div>
            )}
            {status?.yourHighBidUsd != null && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Your bid:</span>
                <UsdCell value={status.yourHighBidUsd} className="text-sm font-medium" />
                {status.isLeading != null && (
                  <span
                    className={
                      status.isLeading
                        ? 'rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800'
                        : 'rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800'
                    }
                  >
                    {status.isLeading ? 'Leading' : 'Outbid'}
                  </span>
                )}
              </div>
            )}
            {closesAt && (
              <div className="flex items-center gap-2">
                <AuctionCountdown closesAt={closesAt} />
              </div>
            )}
          </div>
          {isOpen &&
            (!canBuy ? (
              <ProfileLockedCard side="buyer" missingFields={buyerMissingFields} />
            ) : (
              <button
                type="button"
                onClick={() => setShowBidDialog(true)}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
              >
                <Gavel className="size-4" aria-hidden="true" />
                Place bid
              </button>
            ))}
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Owner-only lot members list — the pre-SIL-1348 layout, kept for the owner
// view (spec 4.2: "the owner view keeps its current layout for owner-only
// controls"). Buyers get `LotContentsTable` instead.
// ---------------------------------------------------------------------------

function OwnerLotMembersSection({ listing }: { listing: ListingDetail }) {
  const members = listing.members ?? [];
  return (
    <section>
      <PanelTitle title="Assets in this lot" className="mb-3" />
      {members.length === 0 ? (
        <Card className="text-center text-sm text-muted-foreground">No assets in this lot.</Card>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {members.map((m, i) => (
            <li key={`${m.assetClassName ?? ''}-${m.conditionBand ?? ''}-${i}`} className="px-4 py-3">
              <p className="truncate text-sm font-medium text-foreground">{m.displayTitle}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {[m.assetClassName, m.conditionBand].filter(Boolean).join(' · ') || '—'}
                {' · '}
                {m.nodeCount} {m.nodeCount === 1 ? 'node' : 'nodes'}
              </p>
            </li>
          ))}
        </ul>
      )}
      {listing.lotMembers != null && listing.lotMembers.length > 0 && (
        <ul className="mt-3 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {listing.lotMembers.map((m) => (
            <li key={m.assetId}>
              <Link
                to="/passport/$fingerprint"
                params={{ fingerprint: m.fingerprint }}
                search={{ listing: listing.id }}
                className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted"
              >
                <div className="min-w-0">
                  <p className="truncate font-mono text-xs text-foreground">
                    {m.chassisSerial ?? m.fingerprint}
                  </p>
                  {/* SIL-1366 — the same badge the buyer table uses. This
                      previously printed the RAW verdict token
                      (`fail:thermal_throttle`) in muted grey: un-reviewed copy,
                      and a failure in a neutral treatment on the view whose
                      owner most needs to see it. The classification and its
                      symptom sentence live on the linked Passport. */}
                  <div className="mt-0.5">
                    <VerdictBadge verdict={m.verdict} />
                  </div>
                </div>
                <span className="text-muted-foreground">&rarr;</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Lot environmental — embodied-carbon total across members (SIL-190).
// Always renders for a lot listing: a computed total, or the pending
// placeholder when no member has a classified footprint yet.
// ---------------------------------------------------------------------------

function LotEnvironmentalSection({ environmental }: { environmental: ListingLotEnvironmental | null }) {
  if (environmental == null) return null;
  const fmtKg = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  return (
    <section>
      <PanelTitle title="Environmental — embodied carbon" description={CARBON_EMBODIED_EXPLAINER} className="mb-3" />
      <Card>
        {environmental.computedAssetCount === 0 ? (
          <p className="text-sm text-muted-foreground">
            Environmental data pending hardware classification
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-num text-3xl font-semibold text-foreground">
                {fmtKg(environmental.totalEmbodiedKgco2eq)}
              </span>
              <span className="text-sm text-muted-foreground">
                kgCO&#8322;eq &middot; range {fmtKg(environmental.totalMin)}&ndash;
                {fmtKg(environmental.totalMax)}
              </span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Lot total &mdash; {environmental.aggregationLabel}.
            </p>
            {environmental.pendingClassificationCount > 0 && (
              <p className="mt-1 text-sm text-amber-700">
                {environmental.pendingClassificationCount} of{' '}
                {environmental.computedAssetCount + environmental.pendingClassificationCount}{' '}
                assets pending hardware classification and excluded from this total.
              </p>
            )}
            {environmental.methodologyVersion && (
              <p className="mt-1 text-xs text-muted-foreground">
                Methodology: Boavizta open-source
              </p>
            )}
            <p className="mt-3 text-xs text-muted-foreground">{environmental.disclaimer}</p>
          </>
        )}
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Derived listing facts. All of these read the SERVER's projection — nothing
// here recomputes a price, a range or a node count from another field.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Buyer detail (SIL-1348) — header card, then the two-column body.
// ---------------------------------------------------------------------------

function BuyerListingDetail({
  listing,
  canBuy,
  buyerMissingFields,
}: {
  listing: ListingDetail;
  canBuy: boolean;
  buyerMissingFields: string[];
}) {
  const { openVerification } = useVerificationGate();
  // SIL-1427: Buy now and Make an offer are now two DISTINCT dialogs — no
  // more shared `offerDialog` state carrying a `prefillUsd` for both (the
  // SIL-1318 interim rule this replaces).
  const [buyNowOpen, setBuyNowOpen] = useState(false);
  const [makeOfferOpen, setMakeOfferOpen] = useState(false);
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  // SIL-1429: open to BOTH buyer tiers (unlike Buy now / Make an offer, no
  // verification gate) — only canBuy (business profile complete) and the
  // `buyerVerification` org feature (business + reseller; individual orgs 403
  // at the route; this is presentation, not the boundary). Read directly off useAuth rather
  // than threading a new prop through the page component and every other
  // caller of BuyerListingDetail.
  const { organizationType } = useAuth();
  const showExpressInterest = canBuy && hasOrgFeature(organizationType, 'buyerVerification');
  // SIL-1349 — the open node Passport drawer, by asset id. Only the id is held:
  // the drawer fetches the node's data itself from `passport-summary`, so the
  // page never becomes a second source for what that route already decided.
  const [nodeDrawerAssetId, setNodeDrawerAssetId] = useState<string | null>(null);

  /**
   * Whether the drawer's control is offered at all. `passport-summary` 403s an
   * unverified buyer, so a "Passport" button that opens it would be a control
   * that cannot work — `LotContentsTable` keeps its `Link` to the (server-gated)
   * full passport route instead, which is its own behaviour without the prop.
   *
   * Read through `useBuyerStatus`, the app's single caller of the
   * buyer-verification endpoint. This is presentation, not enforcement: the API
   * is the boundary.
   */
  const { data: buyerStatus } = useBuyerStatus();
  const canOpenNodePassport = isBuyerVerified(
    buyerStatus?.status,
    buyerStatus?.needsAgreementReacceptance ?? false,
  );

  const nodeCount = nodeCountOf(listing);

  // withdrawn/rejected are terminal — they don't block a new offer (the DB's
  // one-live-offer unique index only covers pending/countered/accepted/
  // deposit_held).
  const myOffer = listing.offers[0] ?? null;
  const offerBlocksNew =
    myOffer != null && myOffer.status !== 'withdrawn' && myOffer.status !== 'rejected';
  // `acceptingOffers` is the server's own mirror of the POST /offers gate
  // (SIL-1345) — the card must not offer a button the API would 409.
  const canTransact = listing.acceptingOffers && !offerBlocksNew && canBuy;
  // SIL-2217: say WHY the card has no buttons. Closed wins (nothing the buyer
  // can fix), then the profile (actionable), then the live offer.
  const offersBlockedReason = !listing.acceptingOffers
    ? 'closed'
    : !canBuy
      ? 'profile_incomplete'
      : 'existing_offer';

  // SIL-1529
  useEffect(() => {
    track('view_item', { item_id: listing.id, auth_state: 'authed' });
  }, [listing.id]);
  const listPriceUsd = listingPriceView(listing.pricing).listPriceUsd;


  return (
    <div className="space-y-6">
      {buyNowOpen && <BuyNowDialog listing={listing} onClose={() => setBuyNowOpen(false)} />}

      {makeOfferOpen && (
        <MakeOfferDialog
          listingId={listing.id}
          pricing={listing.pricing}
          onClose={() => setMakeOfferOpen(false)}
        />
      )}

      {nodeDrawerAssetId !== null && (
        <NodePassportDrawer
          listingId={listing.id}
          assetId={nodeDrawerAssetId}
          onClose={() => setNodeDrawerAssetId(null)}
        />
      )}

      {interestDialogOpen && (
        <ExpressInterestModal listingId={listing.id} onClose={() => setInterestDialogOpen(false)} />
      )}

      <BuyerListingView
        listing={listing}
        backLink={
          <BackLink as={Link} to="/marketplace">
            {LISTING_BACK_LABEL}
          </BackLink>
        }
        renderManufacturerLogo={(l) => (
          <ManufacturerLogo id={l.manufacturerId} name={l.manufacturerName ?? ''} size={32} />
        )}
        onOpenNodePassport={
          canOpenNodePassport ? (member) => setNodeDrawerAssetId(member.assetId) : undefined
        }
        renderLotPassportLink={(member) => (
          <Link
            to="/passport/$fingerprint"
            params={{ fingerprint: member.fingerprint }}
            search={{ listing: listing.id }}
            className="text-xs font-medium text-brand-600 hover:underline"
          >
            {LOT_TABLE_PASSPORT_ACTION}
          </Link>
        )}
        renderPassportSummary={(l) => (
          // A single-node listing's own passport summary. Verified tier only:
          // the strip renders REAL passport content, which is exactly what the
          // unverified Passport card's placeholder stands in for.
          <section>
            <PanelTitle title="Digital passport" className="mb-3" />
            <PassportSummaryStrip fingerprint={l.assetFingerprint ?? ''} listingId={l.id} />
          </section>
        )}
        carbonClaim={
          // SIL-191: avoided-carbon claim - renders nothing when the server
          // suppressed the scalars (low completeness / no snapshot).
          <CarbonClaim
            skuId={listing.skuId}
            upToKgco2eq={listing.carbonAvoidedUpToKgco2eq}
            methodologyVersion={listing.carbonMethodologyVersion}
          />
        }
        leftColumnExtras={
          <>
            <LotEnvironmentalSection environmental={listing.lotEnvironmental} />

            {listing.saleMechanic === 'auction' && (
              <BidSection listing={listing} canBuy={canBuy} buyerMissingFields={buyerMissingFields} />
            )}

            <YourOfferPanel
              listing={listing}
              canBuy={canBuy}
              buyerMissingFields={buyerMissingFields}
            />

            {/* SIL-1429: a separate affordance from ActionCard's own Buy/Offer
                buttons, on purpose - express interest carries no price tier and
                no acceptingOffers gate, so it stays available even for an
                auction listing or a closed one. */}
            {showExpressInterest && (
              <div>
                <button
                  type="button"
                  onClick={() => setInterestDialogOpen(true)}
                  className="w-full rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted sm:w-auto"
                >
                  {EXPRESS_INTEREST_BUTTON}
                </button>
              </div>
            )}
          </>
        }
        action={{
          canTransact,
          offersBlockedReason,
          renderProfileLink: (label) => (
            <Link to="/profile" hash="buying" className="font-medium text-brand-600 hover:underline">
              {label}
            </Link>
          ),
          buyerVerified: canOpenNodePassport,
          onBuyNow: () => {
            track('begin_checkout', { item_id: listing.id, price_band: priceBand(listPriceUsd) });
            setBuyNowOpen(true);
          },
          onMakeOffer: () => setMakeOfferOpen(true),
          onGetVerified: () => openVerification(),
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ListingDetailPage() {
  const { listingId } = Route.useParams();
  const { canBuy, buyerMissingFields } = useAuth();

  const {
    data: listing,
    isLoading,
    error,
    refetch,
  } = useQuery<ListingDetail>({
    queryKey: qk.listings.detail(listingId),
    queryFn: async () => {
      const res = await api.api.client.listings[':id'].$get({ param: { id: listingId } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
      }
      return (await res.json()) as unknown as ListingDetail;
    },
    retry: (failCount, err: unknown) =>
      (err as { status?: number }).status !== 404 && failCount < 2,
  });

  if (isLoading) {
    return <LoadingShell rows={6} />;
  }

  if (error || !listing) {
    return (
      <div className="space-y-4">
        <BackLink as={Link} to="/listings">Listings</BackLink>
        {error ? (
          <ErrorState title="Couldn't load this listing." error={error} onRetry={() => void refetch()} />
        ) : (
          <Card className="text-center text-muted-foreground">Listing not found.</Card>
        )}
      </div>
    );
  }

  if (!listing.isOwner) {
    return (
      <BuyerListingDetail
        listing={listing}
        canBuy={canBuy}
        buyerMissingFields={buyerMissingFields}
      />
    );
  }

  // -------------------------------------------------------------------------
  // Owner (seller) view — unchanged layout for the owner-only controls
  // (spec 4.2). The offers table, the accept/reject actions and the reserve
  // price stay exactly where the seller already expects them.
  // -------------------------------------------------------------------------

  const title =
    listing.displayTitle ??
    listing.skuModelName ??
    listing.skuShortDescription ??
    listing.id.slice(0, 8);

  return (
    <div className="space-y-6">
      <BackLink as={Link} to="/listings">Listings</BackLink>
      <PageHeader
        title={title}
        subtitle={listing.assetClassName ?? `${listing.listingType} listing`}
        actions={<ListingStatusBadge value={listing.status} availableOn={listing.availableOn} />}
      />

      <Card>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Asking price
            </dt>
            <dd className="mt-1 text-base font-num text-foreground">{priceText(listing.pricing)}</dd>
          </div>
          {listing.saleMechanic === 'auction' && listing.auction && (
            <>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Auction closes
                </dt>
                <dd className="mt-1 text-sm text-foreground">{formatDate(listing.auction.closesAt)}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Min bid increment
                </dt>
                <dd className="mt-1">
                  <UsdCell value={listing.auction.minIncrementUsd} className="text-sm" />
                </dd>
              </div>
              {/* SIL-643: OWNER-ONLY. listing.reservePriceUsd is null for anyone
                  but the owning seller — the server (client_listing_auction_
                  reserve_price(), 759) already enforces that; this is purely a
                  rendering choice, not the enforcement point. */}
              {listing.reservePriceUsd != null && (
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Reserve price (private)
                  </dt>
                  <dd className="mt-1">
                    <UsdCell value={listing.reservePriceUsd} className="text-sm" />
                  </dd>
                </div>
              )}
            </>
          )}
          {listing.quantity != null && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Quantity
              </dt>
              <dd className="mt-1 text-sm text-foreground">{listing.quantity}</dd>
            </div>
          )}
          {listing.manufacturerName && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Manufacturer
              </dt>
              <dd className="mt-1 text-sm text-foreground">
                <ManufacturerLogo id={listing.manufacturerId} name={listing.manufacturerName} size={32} />
              </dd>
            </div>
          )}
          {listing.conditionBand && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Condition
              </dt>
              <dd className="mt-1 text-sm text-foreground">{listing.conditionBand}</dd>
            </div>
          )}
          {listing.generation && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Generation
              </dt>
              <dd className="mt-1 text-sm text-foreground">{listing.generation}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Listed
            </dt>
            <dd className="mt-1 text-sm text-foreground">{formatDate(listing.createdAt)}</dd>
          </div>
          {listing.assetSerialNumber && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Serial
              </dt>
              <dd className="mt-1 font-mono text-xs text-foreground">{listing.assetSerialNumber}</dd>
            </div>
          )}
        </dl>
      </Card>

      {listing.saleMechanic === 'auction' && (
        <BidSection
          listing={listing}
          canBuy={canBuy}
          buyerMissingFields={buyerMissingFields}
          isOwnListing
        />
      )}

      {/* Offers table */}
      <div>
        <PanelTitle title="Offers" className="mb-3" />
        {listing.offers.length === 0 ? (
          <Card className="text-center text-sm text-muted-foreground">
            No offers yet.
          </Card>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border bg-card">
            <table className="min-w-full text-sm">
              <thead className="border-b border-border bg-muted">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Buyer
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Amount
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Submitted
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground" />
                </tr>
              </thead>
              <tbody>
                {listing.offers.map((offer) => (
                  <tr key={offer.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 align-middle">
                      {/* SIL-1427: a buy_now row names itself rather than the
                          buyer org — the seller never sees Counter for it
                          (OfferActions below), so the label is the first cue
                          this is a fixed-price request, not a negotiation. */}
                      {offer.kind === 'buy_now' ? (
                        <div className="space-y-0.5">
                          <span className="text-xs font-medium text-foreground">
                            {BUY_NOW_REQUEST_LABEL}
                          </span>
                          {offer.status === 'pending' && (
                            <p className="text-xs text-muted-foreground">
                              {buyNowTimeLeftLabel(offer.expiresAt)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="font-mono text-xs text-foreground">
                          {offerLabel(offer.id)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <UsdCell value={offer.amountUsd} />
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <OfferStatusBadge value={offer.status} />
                    </td>
                    <td className="px-3 py-2 align-middle text-sm text-muted-foreground">
                      {formatDate(offer.createdAt)}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <OfferActions offer={offer} listingId={listingId} availableOn={listing.availableOn} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {listing.listingType === 'lot' ? (
        <>
          <OwnerLotMembersSection listing={listing} />
          <LotEnvironmentalSection environmental={listing.lotEnvironmental} />
        </>
      ) : listing.assetFingerprint ? (
        <section>
          <PanelTitle title="Digital passport" className="mb-3" />
          {/* Summary only — the full §1-§5 document is one click away at
              /passport/:fingerprint?listing=:listingId. */}
          <PassportSummaryStrip fingerprint={listing.assetFingerprint} listingId={listing.id} />
        </section>
      ) : null}

      <EvidencePanel evidence={listing.evidence} />

      {/* SIL-1429: read-only, owner-only, no reply control — spec section 5
          "minimal for this slice". Slice 5 (SIL-1320) moves this into My
          Listings. */}
      <ExpressedInterestsPanel listingId={listing.id} />

      {/* SIL-1471: seller-private, this owner's org only. This whole return
          branch already requires listing.isOwner (guard above) — there is
          no non-owner path that reaches this component. */}
      <ListingNotesPanel listingId={listing.id} />
    </div>
  );
}
